import { nl } from './i18n-nl';
import { en } from './i18n-en';
import { BLOCKS } from './blocks';
import { MODEL_TYPES } from './protocol';

type Dict = Record<string, string>;

const locales: Record<string, Dict> = { nl, en };
let currentLocale = 'nl';

export function getLocale(): string {
  return currentLocale;
}

export function setLocale(locale: string): void {
  if (!locales[locale]) return;
  currentLocale = locale;
  localStorage.setItem('jannecraft-lang', locale);
  updateDOM();
  window.dispatchEvent(new CustomEvent('languagechange'));
}

export function t(key: string, params?: Record<string, string | number>): string {
  let str = locales[currentLocale]?.[key] ?? locales['nl']?.[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(`{${k}}`, String(v));
    }
  }
  return str;
}

export function getBlockName(id: number): string {
  const key = `block.${id}`;
  const translated = locales[currentLocale]?.[key];
  if (translated) return translated;
  return BLOCKS[id]?.name ?? '?';
}

export function getModelLabel(id: number): string {
  const key = `model.${id}`;
  const translated = locales[currentLocale]?.[key];
  if (translated) return translated;
  return MODEL_TYPES.find(m => m.id === id)?.label ?? '?';
}

export function updateDOM(): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n')!;
    el.textContent = t(key);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach(el => {
    const key = el.getAttribute('data-i18n-placeholder')!;
    (el as HTMLInputElement).placeholder = t(key);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach(el => {
    const key = el.getAttribute('data-i18n-html')!;
    el.innerHTML = t(key);
  });
  // Update active state of language buttons
  document.querySelectorAll<HTMLElement>('.lang-btn').forEach(btn => {
    btn.classList.toggle('active', btn.getAttribute('data-lang') === currentLocale);
  });
}

export function initI18n(): void {
  // Priority: ?lang= > localStorage > navigator.language > 'nl'
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang');
  if (urlLang && locales[urlLang]) {
    currentLocale = urlLang;
  } else {
    const stored = localStorage.getItem('jannecraft-lang');
    if (stored && locales[stored]) {
      currentLocale = stored;
    } else {
      const browserLang = navigator.language?.slice(0, 2);
      if (browserLang && locales[browserLang]) {
        currentLocale = browserLang;
      } else {
        currentLocale = 'nl';
      }
    }
  }
  localStorage.setItem('jannecraft-lang', currentLocale);
  updateDOM();
}

// Expose on window for inline scripts
(window as any).JCI18n = { t, getLocale, setLocale, initI18n, getBlockName, getModelLabel, updateDOM };
